import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ConnectedAccountsResponse, GoogleContactsSyncResult } from '@/lib/types'

export function useGoogleContactsAccounts() {
  return useQuery({
    queryKey: ['google-contacts-accounts'],
    queryFn: () => api<ConnectedAccountsResponse>('/api/google-contacts/accounts'),
  })
}

export function useConnectGoogleContacts() {
  return useMutation({
    mutationFn: async () => {
      const { url } = await api<{ url: string }>('/api/google-contacts/connect')
      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(
        url,
        'google-contacts-oauth',
        `width=${width},height=${height},left=${left},top=${top}`,
      )

      return new Promise<void>((resolve, reject) => {
        const interval = setInterval(() => {
          try {
            if (!popup || popup.closed) {
              clearInterval(interval)
              resolve()
              return
            }
            const popupUrl = popup.location.href
            if (popupUrl.includes('/api/google-contacts/callback') || popupUrl.includes('code=')) {
              const urlParams = new URLSearchParams(popup.location.search)
              const code = urlParams.get('code')
              clearInterval(interval)
              popup.close()

              if (code) {
                api('/api/google-contacts/callback', {
                  method: 'POST',
                  body: JSON.stringify({ code }),
                })
                  .then(() => resolve())
                  .catch(reject)
              } else {
                reject(new Error('No authorization code received'))
              }
            }
          } catch {
            // Cross-origin error — popup is still on OAuth provider page
          }
        }, 500)

        setTimeout(() => {
          clearInterval(interval)
          popup?.close()
          reject(new Error('OAuth flow timed out'))
        }, 5 * 60_000)
      })
    },
  })
}

export function useDisconnectGoogleContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      api(`/api/google-contacts/accounts/${accountId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-contacts-accounts'] })
    },
  })
}

export function useSyncGoogleContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      api<GoogleContactsSyncResult>(`/api/google-contacts/accounts/${accountId}/sync`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-contacts-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}
